/* eslint-disable array-callback-return */
import * as yup from "yup";
import regEx from "./regularExpression";
import { validateImageTypes } from "../utils/image-helper";

const validationSchema: any = {
  firstName: yup
    .string()
    .trim("First name cannot include leading and trailing spaces")
    .strict(true)
    .min(2, "First name must be more than 2 characters")
    .max(30, "First name cannot exceed more than 30 characters")
    .required("First name is a required field")
    .matches(regEx.alphabetsOnly, "Only alphabets are allowed for this field"),

  lastName: yup
    .string()
    .matches(regEx.alphabetsOnly, "Only alphabets are allowed for this field")
    .trim("Last name cannot include leading and trailing spaces")
    .strict(true)
    .min(1, "Last name must be more than 1 characters")
    .max(30, "Last name cannot exceed more than 30 characters")
    .required("Last name is a required field"),

  annualIncome: yup
    .number()
    .typeError("Annual income must be a number")
    .positive("Annual income must be a positive number")
    .integer("Annual income must be a whole number")
    .nullable(),

  phone: yup
    .string()
    .strict(true)
    .matches(/^[0-9]+$/, "Phone must only contain digits")
    .min(10, "Phone must be 10 characters")
    .max(10, "Phone must be 10 characters")
    .required("Phone is a required field"),
    
  name: yup
    .string()
    .trim("Name cannot include leading and trailing spaces")
    .strict(true)
    .min(2, "Name must be more than 2 characters")
    .max(32, "Name cannot exceed more than 32 characters")
    .required("Name is a required field")
    .matches(regEx.alphabetsOnly, "Only alphabets are allowed for this field"),

  title: yup.string().required("Title is a required field"),
  gender: yup.string().required("Gender is a required field"),

  validityPeriod: yup.string().required("Validity period is a required field"),

  isValidityPeriod: yup
    .string()
    .required("Is validity period is a required field"),
  email: yup
    .string()
    .required("Email is a required field")
    .trim()
    .email("Invalid Email address format")
    .matches(/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/, "Email must include a valid domain (e.g., .com, .net)")
    .min(3, "Email address must be more than 2 characters")
    .max(30, "Email address cannot exceed more than 30 characters"),


  dob: yup.string().required("Date of birth is a required field"),

  password: yup
    .string()
    .test(
      "betweenSpaces",
      "Password has an excessive number of intermediate spaces",
      (value: any) => {
        if (value?.trim()?.match(regEx.tooManySpace)?.length > 0) {
          return false;
        }
        return true;
      }
    )
    .matches(/^(?!\s).*$/, "Password cannot start or end with spaces")
    .matches(/[A-Z]/, "Password must contain at least one uppercase letter")
    .matches(/[a-z]/, "Password must contain at least one lowercase letter")
    .matches(/\d/, "Password must contain at least one number")
    .matches(
      /[!@#$%^&*()]/,
      "Password must contain at least one special character"
    )
    .min(8, "Password must be more than 8 characters")
    .max(16, "Password cannot exceed more than 16 characters")
    .required("Password is a required field"),

  newPassword: yup
    .string()
    .test(
      "betweenSpaces",
      "New password has an excessive number of intermediate spaces",
      (value: any) => {
        if (value?.trim()?.match(regEx.tooManySpace)?.length > 0) {
          return false;
        }
        return true;
      }
    )
    .matches(/^(?!\s).*$/, "New password cannot start or end with spaces")
    .matches(/[A-Z]/, "New password must contain at least one uppercase letter")
    .matches(/[a-z]/, "New password must contain at least one lowercase letter")
    .matches(/\d/, "New password must contain at least one number")
    .matches(
      /[@$!%*?&]/,
      "New password must contain at least one special character"
    )
    .min(8, "New password must be more than 8 characters")
    .max(16, "New password cannot exceed more than 16 characters")
    .required("New password is a required field"),

  confirmPassword: yup
    .string()
    .required("Confirm password is a required field")
    .test(
      "betweenSpaces",
      "Conform password has an excessive number of intermediate spaces",
      (value: any) => {
        if (value?.trim()?.match(regEx.tooManySpace)?.length > 0) {
          return false;
        } else {
          return true;
        }
      }
    )
    .oneOf(
      [yup.ref("newPassword")],
      "Confirmation password must match the new password"
    ),
  fullName: yup
    .string()
    .trim("Full name cannot include leading and trailing spaces")
    .strict(true)
    .min(2, "Full name must be more than 2 characters")
    .max(250, "Full name cannot exceed more than 30 characters")
    .required("Full name is a required field")
    .matches(regEx.alphabetsOnly, "Only alphabets are allowed for this field"),
  roleID: yup.string().required("Role is a required field"),

  addressLine1: yup
    .string()
    .strict(true)
    .required("Address is a required field")
    .min(2, "Address must be at least 2 characters long."),
  zipCode: yup
    .string()
    .strict(true)
    .required("Zip code is a required field")
    .min(6, "Zip code must be at least 6 characters long."),
  joinedDate: yup
    .string()
    .strict(true)
    .required("Date of joining is a required field"),
  startDate: yup.string().required("Start Date is required"),
  startTime: yup.string().required("Start Time is required"),
  endDate: yup.string().required("End Date is required"),
  endTime: yup.string().required("End Time is required"),
  formImage: yup
    .mixed()
    .nullable()
    .test(
      "FILE_SIZE",
      "Uploaded image is too big. Max size is 2MB",
      (value) => {
        if (value && typeof value !== "string") {
          const file = value as File;
          return file.size <= 2000000;
        }
        return true;
      }
    )
    .test(
      "file-type",
      "Invalid file type. Please select a valid image.",
      (value) => {
        if (!value) return false;
        return validateImageTypes(value as File);
      }
    ),

  location: yup.string().required("Venue is a required field"),

  description: yup.string().required("Description is a required field"),

  remarks: yup.string().required("Remark is required"),

  //state: yup.string().required("Please select a state"),

  country: yup
    .array()
    .required("Country is required")
    .min(1, "Country is required"),
  state: yup
    .array()
    .required("State are required")
    .min(1, "State are required"),
  //country: yup.string().required("Please select a country"),
  city: yup.array().required("City are required").min(1, "City are required"),
  streetName: yup
    .string()
    .trim()
    .matches(
      /^[a-zA-Z0-9\s,.'-]*$/,
      "Street Name can only contain letters, numbers, spaces, and special characters (, . ' -)."
    )
    .min(2, "Street Name must be at least 2 characters long.")
    .max(100, "Street Name cannot be more than 100 characters.")
    .required("Street Name is required."),
  signatureText: yup.string().required("Signature is required"),

  industryId: yup.string().required("Industry is a required field"),
  subdomainName: yup
    .string()
    .required("Subdomain Name is a required field")
    .matches(
      regEx.subdomain,
      "Invalid subdomain. Only letters, numbers, and hyphens are allowed, and it must start and end with a letter or number."
    )
    .min(3, "Subdomain name must be at least 3 characters long.")
    .max(63, "Subdomain name must not exceed 50 characters."),

  rolePermissions: yup
    .array()
    .min(1, "At least one permission must be selected")
    .required("Role permissions are required"),
};

export const getValidationSchema = (fields: string[]) => {
  const newValidationSchema: any = {};

  fields.map((fieldName) => {
    newValidationSchema[fieldName] = validationSchema[fieldName];
  });

  return yup.object().shape(newValidationSchema);
};
